import React, { useContext } from "react";
import PropTypes from "prop-types";
import { isEmpty } from "lodash";
import { priceConstructor } from "../../utils/general";
import {
  productHasOtherVariants,
  constructImageGalleryItems,
  getMetafield,
  getShopifyProductImageAltText,
  getShopifyProductUpsellImage,
  sanitizeMarkup,
  getVariantData,
} from "../../utils/shop";
import { EVENT_PROPERTIES_VALUES } from "../../types/Amplitude";
import {
  RECOMMENDED_PRODUCT_DESCRIPTION_KEY,
  RECOMMENDED_PRODUCT_IMAGE_KEY,
} from "../../utils/productConstants";

// Components
import Paragraph from "../Atoms/Paragraph";
import ShopifyAddToCartCta from "../Utils/ShopifyAddToCartCta";
import {
  ImageGalleryContext,
  ImageGalleryThumbnails,
  ImageGalleryFeaturedImage,
} from "./ImageGallery";
import { ImgixImage } from "../Utils/ImgixImage";

// Stylesheets
import "./ShopifyRecommendedProducts.scss";

export const renderDescription = (description) => {
  if (description) {
    return (
      <Paragraph className="description caption_tertiary">
        <span
          dangerouslySetInnerHTML={{ __html: sanitizeMarkup(description) }}
        />
      </Paragraph>
    );
  }

  return null;
};

export const renderAddToCartCta = (productShopifyData, variantId) => {
  if (!productShopifyData || isEmpty(productShopifyData)) {
    return null;
  }

  return (
    <ShopifyAddToCartCta
      productShopifyData={productShopifyData}
      variantId={variantId}
      buttonProps={{ buttonType: "secondary" }}
      trackingProps={{
        sectionName: EVENT_PROPERTIES_VALUES.Recommended_Products,
      }}
    >
      Add
    </ShopifyAddToCartCta>
  );
};

export const Product = ({ shopifyProductData }) => {
  const src = getShopifyProductUpsellImage(shopifyProductData);
  const altText = getShopifyProductImageAltText(shopifyProductData);
  const description = getMetafield(
    shopifyProductData,
    RECOMMENDED_PRODUCT_DESCRIPTION_KEY
  );
  const price = getVariantData(shopifyProductData).price;

  return (
    <div className="product">
      <div className="details_wrapper">
        <ImgixImage
          className="featured_image"
          sizes={{ default: "96px", desktop: "120px" }}
          src={src}
          // Note: shopifyProductData.title could have HTML in it, but it's the
          // best field to default to as it has the product name in it.
          // just need to make sure altText is added for every image in Shopify
          alt={altText || shopifyProductData.title}
        />

        <div className="details">
          <header className="header">
            <h6 className="title h6_text">
              <span
                dangerouslySetInnerHTML={{
                  __html: sanitizeMarkup(shopifyProductData.title),
                }}
              />
            </h6>

            {renderDescription(description)}
          </header>

          <span className="price">{priceConstructor({ price })}</span>
        </div>
      </div>

      {renderAddToCartCta(shopifyProductData)}
    </div>
  );
};

Product.propTypes = {
  shopifyProductData: PropTypes.object.isRequired,
};

export const ProductWithImageGallery = ({ shopifyProductData }) => {
  const { selectedId } = useContext(ImageGalleryContext);
  const selectedVariant = shopifyProductData.variants[selectedId];
  const description = getMetafield(
    selectedVariant,
    RECOMMENDED_PRODUCT_DESCRIPTION_KEY
  );
  const { thumbnailImages, featuredImages } = constructImageGalleryItems({
    productShopifyData: shopifyProductData,
    featuredImageKey: RECOMMENDED_PRODUCT_IMAGE_KEY,
  });

  return (
    <div className="product has_image_gallery">
      <div className="details_wrapper image_gallery">
        <ImageGalleryFeaturedImage
          sizes={{ default: "96px", desktop: "120px" }}
          images={featuredImages}
        />

        <div className="details">
          <header className="header">
            <h6 className="title h6_text">{shopifyProductData.title}</h6>

            <Paragraph className="description caption_tertiary">
              {description}
            </Paragraph>
          </header>

          <ImageGalleryThumbnails
            shape="circle"
            sizes={{ default: "24px" }}
            thumbnailImages={thumbnailImages}
          />

          <span className="price">
            {priceConstructor({
              price: selectedVariant.price,
              units: "dollars",
              showDollarSign: true,
            })}
          </span>
        </div>
      </div>

      {renderAddToCartCta(shopifyProductData, selectedVariant.id)}
    </div>
  );
};

ProductWithImageGallery.propTypes = {
  shopifyProductData: PropTypes.object.isRequired,
};

const renderProduct = (recommendedProduct) => {
  const hasVariants = productHasOtherVariants(recommendedProduct);
  if (hasVariants) {
    return <ProductWithImageGallery shopifyProductData={recommendedProduct} />;
  }

  return <Product shopifyProductData={recommendedProduct} />;
};

export const ShopifyRecommendedProducts = ({ recommendedProduct }) => {
  return (
    <section
      className="shopify_recommended_products_section"
      data-testid="shopify_recommended_products"
    >
      {renderProduct(recommendedProduct)}
    </section>
  );
};

ShopifyRecommendedProducts.propTypes = {
  recommendedProduct: PropTypes.object.isRequired,
};

export default ShopifyRecommendedProducts;
